import React from 'react';
import { useNavigate } from 'react-router-dom';
import { PlusIcon, WrenchScrewdriverIcon } from '@heroicons/react/24/outline';
import { UseFormRegister, FieldErrors } from 'react-hook-form';

import { Tenancy } from '../../../types/tenancyTypes';
import MaintenanceRequestCard, {
  MaintenanceRequest
} from './MaintenanceRequestCard';
import BackHeader from '../../common/BackHeader';
import MaintenanceRequestForm, {
  MaintenanceRequestFormData
} from './MaintenanceRequestForm';

interface TenantMaintenanceViewProps {
  tenancies: Tenancy[];
  isLoading: boolean;
  requests: MaintenanceRequest[];
  filteredRequests: MaintenanceRequest[];
  page?: number;
  hasNext?: boolean;
  onPageChange?: (targetPage: number) => void;
  isLoadingPage?: boolean;
  showForm: boolean;
  setShowForm: (show: boolean) => void;
  isSubmitting: boolean;
  photoPreviews: string[];
  handleRemovePhoto: (index: number) => void;
  onSubmit: (data: MaintenanceRequestFormData) => void;
  register: UseFormRegister<MaintenanceRequestFormData>;
  errors: FieldErrors<MaintenanceRequestFormData>;
  handleSubmit: any;
  maintenanceCategories: string[];
}

const TenantMaintenanceView: React.FC<TenantMaintenanceViewProps> = ({
  tenancies,
  isLoading,
  requests,
  filteredRequests,
  page = 1,
  hasNext = false,
  onPageChange,
  isLoadingPage = false,
  showForm,
  setShowForm,
  isSubmitting,
  photoPreviews,
  handleRemovePhoto,
  onSubmit,
  register,
  errors,
  handleSubmit,
  maintenanceCategories
}) => {
  const navigate = useNavigate();
  const handleBack = () => navigate(-1);

  if (tenancies.length === 0 && !isLoading) {
    return (
      <div>
        <BackHeader title="My Maintenance Requests" onBack={handleBack} />
        <div className="text-center py-16 px-6 bg-gray-50 rounded-lg border-2 border-dashed border-gray-200">
          <WrenchScrewdriverIcon className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-4 text-lg font-medium text-gray-900">
            No Active Tenancy Found
          </h3>
          <p className="mt-2 text-sm text-gray-500 max-w-xl mx-auto">
            To submit a maintenance request, your account must be linked to an
            active tenancy by your landlord. If you have recently signed a
            lease, please wait for your landlord to set up your account.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <BackHeader
          title="My Maintenance Requests"
          onBack={handleBack}
          className="mb-0"
        />
        <button
          onClick={() => setShowForm(!showForm)}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors shadow-sm"
        >
          <PlusIcon className="w-5 h-5" />
          {showForm ? 'Cancel' : 'New Request'}
        </button>
      </div>

      {showForm && (
        <MaintenanceRequestForm
          tenancies={tenancies}
          isSubmitting={isSubmitting}
          photoPreviews={photoPreviews}
          handleRemovePhoto={handleRemovePhoto}
          onSubmit={onSubmit}
          register={register}
          errors={errors}
          handleSubmit={handleSubmit}
          maintenanceCategories={maintenanceCategories}
        />
      )}

      {!showForm && requests.length === 0 && (
        <div className="text-center py-16 px-6 bg-gray-50 rounded-lg border-2 border-dashed border-gray-200">
          <WrenchScrewdriverIcon className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-4 text-lg font-medium text-gray-900">
            Report an issue with your property
          </h3>
          <div className="mt-4 max-w-2xl mx-auto text-sm text-gray-500">
            <p className="mb-4">
              Need something fixed? Let your landlord know by creating a
              maintenance request.
            </p>
            <ol className="text-left list-decimal list-inside space-y-2">
              <li>
                Click the <strong>'New Request'</strong> button to open the
                form.
              </li>
              <li>
                Select your property, categorize the issue, and describe what's
                wrong.
              </li>
              <li>
                Upload photos to help your landlord understand the problem.
              </li>
              <li>
                Submit the request. You can track its status right here on this
                page.
              </li>
            </ol>
          </div>
          <div className="mt-6">
            <button
              onClick={() => setShowForm(true)}
              className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              <PlusIcon className="-ml-1 mr-2 h-5 w-5" />
              Create Your First Request
            </button>
          </div>
        </div>
      )}

      {requests.length > 0 && (
        <>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {filteredRequests.map((req) => (
              <MaintenanceRequestCard
                key={req.id}
                request={req}
                userRole="tenant"
                propertyTitle={
                  tenancies.find((t) => t.listingId === Number(req.listingId))
                    ?.propertyInfo.title || ''
                }
              />
            ))}
          </div>
          {/* Pagination */}
          <div className="mt-6 flex justify-center gap-2">
            {page > 1 && (
              <button
                onClick={() => onPageChange && onPageChange(page - 1)}
                className="px-3 py-2 text-sm rounded-lg border bg-white hover:bg-gray-50"
              >
                Previous
              </button>
            )}
            <span className="px-3 py-2 text-sm text-gray-600">Page {page}</span>
            {hasNext && (
              <button
                onClick={() => onPageChange && onPageChange(page + 1)}
                className="px-3 py-2 text-sm rounded-lg bg-blue-600 text-white hover:bg-blue-700"
              >
                Next
              </button>
            )}
          </div>
          {isLoadingPage && (
            <div className="mt-2 text-center text-sm text-gray-500">
              Loading…
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default TenantMaintenanceView;
